import React, {useEffect, useMemo, useState} from 'react';
import {BadgeFrameType, Community} from '../../models/community';
import {getDarkestRgbColor, getDarkerShade, getLighterShade} from '../../utils/color-util';
import {buildCommunityBadgePixelArt, hashCommunityBadgeSeed} from '../../utils/community-badge-util';

type CommunityBadgeVariant = 'circle' | 'rounded' | 'shield';

interface CommunityBadgeProps {
  badgeFrameType?: BadgeFrameType | string;
  badgeUrl?: string;
  name: string;
  size?: number;
  style?: React.CSSProperties;
  variant?: CommunityBadgeVariant;
}

const DEFAULT_SIZE = 40;
const PIXEL_GRID_SIZE = 10;

export const CommunityBadge: React.FC<CommunityBadgeProps> = ({
  badgeFrameType = BadgeFrameType.SHIELD,
  badgeUrl,
  name,
  size = DEFAULT_SIZE,
  style,
  variant = 'shield',
}) => {
  const displayName = name || 'community';
  const uri = badgeUrl;
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const {bg, pixels, palette} = useMemo(() => buildCommunityBadgePixelArt(displayName, PIXEL_GRID_SIZE), [displayName]);
  const pixelSize = 400 / PIXEL_GRID_SIZE;
  const clipId = useMemo(() => `shield-center-${hashCommunityBadgeSeed(displayName)}-${size}`, [displayName, size]);
  const normalizedBadgeFrameType =
    badgeFrameType === BadgeFrameType.NONE || badgeFrameType === 'NONE' ? BadgeFrameType.NONE : BadgeFrameType.SHIELD;
  const shouldShowFrame = variant === 'shield' && normalizedBadgeFrameType !== BadgeFrameType.NONE;
  const width = variant === 'shield' && shouldShowFrame ? size * 1.4 : size;
  const darkestAccent = useMemo(() => getDarkestRgbColor(palette.accents), [palette.accents]);
  const frameColor = darkestAccent;
  const frameBorderColor = getDarkerShade(darkestAccent);
  const frameRibbonColor = getLighterShade(darkestAccent, 1.2);
  const borderRadius = variant === 'circle' ? size / 2 : 10;
  const shouldRenderPlainImage = normalizedBadgeFrameType === BadgeFrameType.NONE && Boolean(uri) && !imageLoadFailed;

  useEffect(() => {
    setImageLoadFailed(false);
  }, [normalizedBadgeFrameType, uri]);

  if (shouldRenderPlainImage) {
    return (
      <div
        className="inline-flex items-center justify-center bg-white overflow-hidden flex-shrink-0"
        style={{
          width,
          height: size,
          borderRadius,
          borderWidth: 0,
          borderColor: 'transparent',
          boxShadow: 'none',
          ...style,
        }}>
        <img src={uri} alt={`${displayName} badge`} className="w-full h-full object-cover" onError={() => setImageLoadFailed(true)} />
      </div>
    );
  }

  const innerContent = uri ? (
    <image href={uri} width={400} height={400} preserveAspectRatio="xMidYMid slice" opacity={1} />
  ) : (
    pixels.map((pixel, index) => (
      <rect
        key={`${pixel.x}-${pixel.y}-${index}`}
        x={pixel.x * pixelSize}
        y={pixel.y * pixelSize}
        width={pixelSize}
        height={pixelSize}
        fill={pixel.color}
      />
    ))
  );

  return (
    <div
      className="inline-flex items-center justify-center bg-white overflow-hidden flex-shrink-0"
      style={{
        width,
        height: size,
        borderRadius,
        borderWidth: 0,
        borderColor: 'transparent',
        boxShadow: 'none',
        ...style,
      }}>
      <svg
        width={width}
        height={size}
        viewBox="0 0 400 400"
        preserveAspectRatio={variant === 'shield' && shouldShowFrame ? 'none' : 'xMidYMid meet'}>
        {variant === 'shield' ? (
          <>
            <defs>
              <clipPath id={clipId}>
                <path d="M200,72 L265,95 L265,200 Q265,265 200,305 Q135,265 135,200 L135,95 Z" />
              </clipPath>
            </defs>
            {shouldShowFrame && (
              <>
                <path d="M200,50 L285,80 L285,200 Q285,285 200,335 Q115,285 115,200 L115,80 Z" fill={frameColor} />
                <path d="M200,65 L273,89 L273,200 Q273,275 200,320 Q127,275 127,200 L127,89 Z" fill="#FFFFFF" />
              </>
            )}
            <g clipPath={shouldShowFrame ? `url(#${clipId})` : undefined}>
              <rect width={400} height={400} fill={bg} />
              {innerContent}
            </g>
            {shouldShowFrame && (
              <>
                <path d="M105,285 L140,295 L140,325 L105,315 Z" fill={frameBorderColor} />
                <path d="M295,285 L260,295 L260,325 L295,315 Z" fill={frameBorderColor} />
                <path d="M120,295 Q200,345 280,295 L280,330 Q200,380 120,330 Z" fill={frameRibbonColor} />
              </>
            )}
          </>
        ) : (
          <>
            <rect width={400} height={400} fill={bg} />
            {innerContent}
          </>
        )}
      </svg>
    </div>
  );
};

interface CommunityBadgeFromCommunityProps {
  community: Community;
  size?: number;
  style?: React.CSSProperties;
  variant?: CommunityBadgeVariant;
}

export const CommunityBadgeFromCommunity: React.FC<CommunityBadgeFromCommunityProps> = ({
  community,
  size = DEFAULT_SIZE,
  style,
  variant = 'shield',
}) => (
  <CommunityBadge
    badgeFrameType={community.badgeFrameType}
    badgeUrl={community.badgeUrl || undefined}
    name={community.name || community.id}
    size={size}
    style={style}
    variant={variant}
  />
);
